import { GeckoFeature } from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

const { pi } = forecastMath

export type FlatCircularFloorAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.FlatCircularFloor
  radius: number
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  features: Record<string, GeckoVerticalWeldJSON>
}

export const FlatCircularFloorAvailableFeatures: GeckoFeatureModelType[] = []

export const NewFlatCircularFloorAssetModelJSON: FlatCircularFloorAssetModelJSON =
  {
    name: 'New Flat Circular Floor',
    type: GeckoComponentModelType.FlatCircularFloor,
    radius: 0,
    features: {},
    scene_config: {},
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: 0,
      y_end: 0,
    },
  }

export class GeckoFlatCircularFloor extends GeckoEditableComponent {
  type: GeckoComponentModelType.FlatCircularFloor
  radius: GeckoMeasurement
  features: Map<string, GeckoFeature>
  sceneConfig = undefined
  componentBounds: GeckoComponentRect

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    componentBounds: GeckoComponentRect
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.FlatCircularFloor
    this.radius = radius
    this.componentBounds = componentBounds

    // Features
    this.features = new Map()
  }

  public static CreateFromJSON(
    id: string,
    json: FlatCircularFloorAssetModelJSON
  ) {
    const name = json.name
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    return new GeckoFlatCircularFloor(id, json, name, radius, componentBounds)
  }

  public asJSON(): FlatCircularFloorAssetModelJSON {
    return generateFlatCircularFloor(
      this.name,
      this.radius,
      this.json as FlatCircularFloorAssetModelJSON
    )
  }
}

const generateFlatCircularFloor = (
  name: string,
  radius: GeckoMeasurement,
  otherData: FlatCircularFloorAssetModelJSON = NewFlatCircularFloorAssetModelJSON
): FlatCircularFloorAssetModelJSON => {
  const tubeRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeCircumference = Number(
    (2 * pi * tubeRadius).toPrecision(assetModelPrecision)
  )

  return {
    name: name,
    type: GeckoComponentModelType.FlatCircularFloor,
    radius: tubeRadius,
    scene_config: otherData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tubeCircumference,
      y_end: tubeCircumference,
    },
    features: otherData.features,
  }
}

export const FlatCircularFloorAssetModelFormFields = (
  JSON: FlatCircularFloorAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Flat Circular Floor Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },

    {
      dataKey: 'radius',
      label: 'Diameter',
      value: JSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const FlatCircularFloorJSONFromFormFields = (
  component: FlatCircularFloorAssetModelJSON,
  formFields: FormField[]
) => {
  return generateFlatCircularFloor(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
